import {
  /*useImperativeHandle,*/ ComponentProps,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
// import { useTranslation } from 'react-i18next';
import { Channel, deviceUtils, useChannelContext } from 'scbk-services';
import { HiddenField, LineIcon, cn, useSecurityFieldLabelContext } from 'scbk-ui-web';

export interface SecurityFieldFullProps extends ComponentProps<'input'> {
  type?: 'default' | 'segmented';
  fieldIdx?: number[];
  id: string;
  maxLength: number;
  open: () => void;
  isOpen?: boolean;
  driverId: 'native' | 'virtual' | 'astx2' | undefined;
  error?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  label?: string;
  isForceOpen?: boolean;
}

const SecurityFieldFull = forwardRef<HTMLInputElement, SecurityFieldFullProps>(
  (
    {
      type = 'default',
      fieldIdx,
      id,
      maxLength,
      error,
      open,
      driverId,
      isOpen,
      disabled = false,
      readonly = false,
      label: propLabel,
      isForceOpen = false,
      ...props
    },
    ref,
  ) => {
    // const { t } = useTranslation();
    const [uiValue, setUiValue] = useState<string>('');
    const [focused, setFocused] = useState<boolean>(false);

    const isDesktop = deviceUtils.isPC();

    const isMouseInteractionRef = useRef(false);

    const { channel } = useChannelContext();
    const isIB = channel === Channel.IB;

    const editableIndexes =
      type === 'segmented' ? fieldIdx : Array.from({ length: maxLength }).map((_, idx) => idx);

    // value값에 매칭되는 index값 반환
    const getSlotIdx = (slotNum: number) => {
      return editableIndexes?.indexOf(slotNum) ?? -1;
    };

    // 해당칸의 value값 반환
    const getSlotChar = (slotNum: number) => {
      const editableOrder = getSlotIdx(slotNum);

      return uiValue[editableOrder] ?? null;
    };

    /**
     *
     * @param onValueChanges 값 변경시 감지
     * @returns
     */
    const detectiveElementValueChange = (onValueChanges: (value: string) => void) => {
      const el = document.getElementById(id);
      if (!el) {
        return;
      }

      const proto = Object.getPrototypeOf(el);
      const desc = Object.getOwnPropertyDescriptor(proto, 'value');

      if (!desc?.get || !desc?.set) return () => {};

      const originalGet = desc.get;
      const originalSet = desc.set;

      Object.defineProperty(el, 'value', {
        configurable: true,
        get() {
          return originalGet.call(this);
        },
        set(v: string) {
          originalSet.call(this, v);
          onValueChanges(String(v ?? ''));
        },
      });

      return () => {
        Object.defineProperty(el, 'value', {
          configurable: true,
          get: originalGet,
          set: originalSet,
        });
      };
    };

    /**
     * 키패드 open
     */
    const handleKeypadOpen = () => {
      isMouseInteractionRef.current && setFocused(true);
      open?.();
      if (driverId === 'astx2') {
        // hidden 입력input과 동기화 clear 해주기
        setUiValue('');
      }
    };

    useEffect(() => {
      if (driverId === 'astx2') {
        // astx 입력 값
        const el = document.getElementById(id) as HTMLInputElement;
        if (!el) return;

        const detective = detectiveElementValueChange((value) => {
          setUiValue(value);
          props?.onChange?.({
            target: {
              value,
            },
            currentTarget: {
              value,
            },
          } as React.ChangeEvent<HTMLInputElement>);
        });

        const handleKeyup = () => {
          setUiValue(el.value);
        };

        el.addEventListener('keyup', handleKeyup);

        if ((isOpen && !isIB) || isForceOpen) {
          // 키패드 오픈
          setTimeout(() => {
            handleKeypadOpen();
          }, 500);
        }

        return () => {
          el.removeEventListener('keyup', handleKeyup);
          detective;
        };
      } else {
        // 가상키패드(OpenWebNFilter), 보안키패드(NFilter)
        const detective = detectiveElementValueChange((value) => {
          setUiValue(value);
          props?.onChange?.({
            target: {
              value,
            },
            currentTarget: {
              value,
            },
          } as React.ChangeEvent<HTMLInputElement>);
        });

        if ((isOpen && !isIB) || isForceOpen) {
          // 키패드 오픈
          setTimeout(() => {
            handleKeypadOpen();
          }, 500);
        }

        return detective;
      }
    }, []);

    const { label: contextLabel } = useSecurityFieldLabelContext();
    const label = propLabel ?? contextLabel;

    return (
      <div
        className="relative security-wrap"
        onMouseDown={(e) => {
          isMouseInteractionRef.current = true;
          e.preventDefault();
        }}
        onClick={() => {
          handleKeypadOpen();
        }}
      >
        <input
          {...props}
          ref={ref}
          id={id}
          type="password"
          inputMode="none"
          className={cn(
            'caret-[transparent] text-[transparent] outline-none absolute inset-0 cursor-default selection:text-[transparent] not-readonly peer not-readonly',
            !isMouseInteractionRef.current &&
              isDesktop &&
              'focus-visible:outline focus-visible:outline-offset-4 focus-visible:outline-black focusVisible',
            maxLength === 4 && fieldIdx?.includes(0) && isDesktop && 'w-[120px] left-0 right-auto',
            maxLength === 4 && fieldIdx?.includes(3) && isDesktop && 'w-[120px] right-0 left-auto',
          )}
          maxLength={fieldIdx ? fieldIdx.length : maxLength}
          title={`${label ? label : type === 'segmented' ? `${fieldIdx?.map((idx) => idx + 1).join(',')}번째 입력` : `${maxLength}자리 입력`} `}
          data-split={true}
          style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none', userSelect: 'none' }}
          onSelect={(e) => {
            const target = e.currentTarget;
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const end = target.value.length;
                target.setSelectionRange(end, end);
              });
            });

            setTimeout(() => {
              const end = target.value.length;
              target.setSelectionRange(end, end);
            }, 50);
          }}
          onFocus={(e) => {
            const target = e.currentTarget;
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const end = target.value.length;
                target.setSelectionRange(end, end);
              });
            });

            setTimeout(() => {
              const end = target.value.length;
              target.setSelectionRange(end, end);
            }, 50);
            handleKeypadOpen();
          }}
          onTouchStart={(e) => {
            e.preventDefault();
          }}
          onBlur={() => {
            console.log('### on Blur');
            setFocused(false);
            isMouseInteractionRef.current = false;
          }}
        />

        <div className="flex gap-2 justify-center" aria-hidden="true">
          {Array.from({ length: maxLength }).map((_, idx) => {
            return type === 'segmented' && !fieldIdx?.includes(idx) ? (
              <HiddenField key={`hidden-field-${idx}`} />
            ) : (
              <div
                className="relative"
                key={idx}
                title={`${getSlotChar(idx) ? `${label ? label : ''} ${idx + 1}번째 입력됨` : `${label ? label + '' : ''} ${idx + 1}번째 입력`}`}
              >
                <div
                  onClick={() => {
                    // 키패드 open
                    handleKeypadOpen();
                  }}
                  className={cn(
                    'w-[clamp(40px,12vw,48px)] h-[48px] rounded-[8px] p-0 border border-basic-04 flex items-center justify-center',
                    isDesktop && 'w-[56px] h-[56px]',
                    error && 'border-2  border-interactive-danger',
                    getSlotChar(idx) && 'border-2 bg-primary-background-07 border-primary-03',
                    focused &&
                      // uiValue.length !== 0 &&
                      uiValue.length === getSlotIdx(idx) &&
                      'border-interactive-primary-default-01',
                    !isMouseInteractionRef.current &&
                      isDesktop &&
                      focused &&
                      uiValue.length === getSlotIdx(idx) &&
                      ' group-has-[input:focus-within]:border-basic-04',
                    disabled && 'bg-state-disabled-01 border-state-disabled-02',
                    readonly && 'bg-state-disabled-01 border-state-disabled-02',
                  )}
                >
                  {getSlotChar(idx) && (
                    <span
                      className={cn(
                        'inline-flex items-center justify-center w-6 h-6 text-primary-05 heading-h2',
                        readonly && 'text-[var(--color-bg-basic-background-01)]',
                      )}
                      style={
                        readonly
                          ? {
                              color: 'var(--color-bg-basic-background-01)',
                            }
                          : undefined
                      }
                    >
                      <LineIcon
                        icoName="ico-pin"
                        icoColor={
                          disabled || readonly
                            ? 'var(--color-bg-basic-background-01)'
                            : 'var(--color-text-primary-05)'
                        }
                      />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div id={`${id}-visual-keypad-anchor`} />
      </div>
    );
  },
);

export { SecurityFieldFull };
